import { UserAvatar } from "@/components/ui/stacked-avatars";
import {
  useConnectActivity,
  type ConnectActivityResult,
} from "@/lib/api/activities";
import { ShareableEntity } from "@/lib/api/shareable-entities";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { PeriwinkleCard } from "@/components/conversations/activities/connect-moment/periwinkle-card";

interface ConnectMomentHubCardProps {
  entity: ShareableEntity;
  isNew: boolean;
  onClick?: () => void;
  className?: string;
}

const computeLevelsCompleted = (result: ConnectActivityResult): number =>
  result.levels.filter((l) => l !== null).length;

const STACKED_CARDS = [
  { rotate: -10, left: -2, z: 0, opacity: 0.55 },
  { rotate: -2, left: 8, z: 1, opacity: 0.8 },
  { rotate: 8, left: 18, z: 2, opacity: 1 },
];

const StackedCardsThumb = () => (
  <div className="relative h-20 w-16 shrink-0">
    {STACKED_CARDS.map((card, i) => (
      <PeriwinkleCard
        key={i}
        variant="highlight"
        fullBleedOverlay
        className="absolute h-16 w-12 rounded-md shadow-sm"
        style={{
          top: 8,
          left: card.left,
          transform: `rotate(${card.rotate}deg)`,
          opacity: card.opacity,
          zIndex: card.z,
          border: "1px solid rgba(255,255,255,0.4)",
        }}
      />
    ))}
  </div>
);

export function ConnectMomentHubCard({
  entity,
  isNew,
  onClick,
  className,
}: ConnectMomentHubCardProps) {
  const { data: activity } = useConnectActivity(entity.id);

  const isScheduled = entity.status === "scheduled" && entity.scheduledAt;
  const formattedDate = isScheduled
    ? `Scheduled, ${DateTime.fromISO(entity.scheduledAt!).toFormat("MMM d")}`
    : DateTime.fromJSDate(new Date(entity.created_at)).toFormat("EEE, h:mm a");

  const status = activity?.status ?? "sent";
  const result = activity?.result;
  const levelsCompleted = result ? computeLevelsCompleted(result) : 0;

  const statusLabel =
    status === "completed"
      ? "Completed"
      : status === "in_progress"
        ? `Level ${Math.min(levelsCompleted + 1, 3)} of 3`
        : "Sent";

  const creatorName = entity.owner.name;
  const creatorAvatarUrl = entity.owner.profile_picture_url ?? undefined;

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50",
        className,
      )}
    >
      <div className="relative h-20 w-16 shrink-0">
        <StackedCardsThumb />
        <div className="absolute -bottom-1 -right-1 z-10">
          <UserAvatar
            imageUrl={creatorAvatarUrl}
            name={creatorName}
            size={28}
            showBorder={true}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary">Connection cards</span>
          <span className="h-1 w-1 rounded-full bg-text-tertiary" />
          <span className="text-sm text-text-tertiary">{formattedDate}</span>
        </div>
        <p className="truncate text-sm text-tertiary">{statusLabel}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isNew && <span className="h-2 w-2 rounded-full bg-purple-600" />}
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
}
